import React from "react";

import { PACKS } from "../Form";
import { POSTAL_CODE_PATTERN } from "../../hooks/useSignup";

const postalCodeError = (postalCode) =>
  postalCode && !POSTAL_CODE_PATTERN.test(postalCode)
    ? "Code postal invalide"
    : "";

const inputClassName = (hasError) =>
  `w-full rounded-lg border bg-white px-3 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
    hasError ? "border-red-500" : "border-gray-300"
  }`;

const Field = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  autoComplete,
  inputMode,
  error,
  className = "",
}) => (
  // The error sits outside the <label> so it never becomes part of the
  // field's name
  <div className={className}>
    <label
      htmlFor={`signup-${name}`}
      className="mb-1 block text-sm font-medium text-gray-700"
    >
      {label}
    </label>
    <input
      id={`signup-${name}`}
      className={inputClassName(Boolean(error))}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      inputMode={inputMode}
    />
    {error ? <span className="mt-1 block text-xs text-red-600">{error}</span> : null}
  </div>
);

const Consent = ({ name, checked, onChange, children }) => (
  <label className="flex items-start gap-3 text-sm text-gray-700">
    <input
      type="checkbox"
      name={name}
      checked={checked}
      onChange={onChange}
      className="mt-0.5 h-5 w-5 shrink-0 accent-yellow-600"
    />
    <span>{children}</span>
  </label>
);

// The signup form, white card. State and submit behaviour come from
// useSignup; this component only draws it.
const SignupCard = ({
  inputs,
  error,
  phoneNumberError,
  isSubmitting,
  isComplete,
  handleChange,
  submitForm,
}) => (
  <form
    onSubmit={submitForm}
    className="w-full rounded-2xl bg-white p-5 shadow-2xl shadow-black/40 sm:p-7"
    noValidate
  >
    <div className="mb-4 flex items-baseline justify-between">
      <h2 className="text-lg font-bold text-gray-900">M'abonner</h2>
      <span className="rounded-full bg-yellow-600 px-3 py-1 text-xs font-bold text-gray-900">
        {PACKS[0].label}
      </span>
    </div>

    <div className="space-y-3">
      <Field label="Nom" name="name" value={inputs.name} onChange={handleChange} autoComplete="name" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Ville" name="city" value={inputs.city} onChange={handleChange} autoComplete="address-level2" />
        <Field
          label="Code Postal"
          name="postal_code"
          value={inputs.postal_code}
          onChange={handleChange}
          autoComplete="postal-code"
          error={postalCodeError(inputs.postal_code)}
        />
      </div>
      <Field label="Adresse" name="address" value={inputs.address} onChange={handleChange} autoComplete="street-address" />
      <Field label="Appartement" name="apartment" value={inputs.apartment} onChange={handleChange} />

      <div>
        <label
          htmlFor="signup-phone_number"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Téléphone #
        </label>
        <div className="flex">
          <span className="flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-700">
            {process.env.REACT_APP_DEFAULT_AREA_CODE}
          </span>
          <input
            id="signup-phone_number"
            className={`${inputClassName(Boolean(phoneNumberError))} rounded-l-none`}
            type="tel"
            inputMode="numeric"
            name="phone_number"
            value={inputs.phone_number}
            onChange={handleChange}
            autoComplete="tel-national"
          />
        </div>
        {phoneNumberError ? (
          <span className="mt-1 block text-xs text-red-600">{phoneNumberError}</span>
        ) : null}
      </div>

      <Field label="E-mail" name="email" type="email" value={inputs.email} onChange={handleChange} autoComplete="email" />
    </div>

    <div className="mt-4 space-y-3">
      <Consent name="accept_sms_notification" checked={inputs.accept_sms_notification} onChange={handleChange}>
        J'accepte de recevoir des alertes de spectacles et autres opportunités par texto
      </Consent>
      <Consent name="accept_email_notification" checked={inputs.accept_email_notification} onChange={handleChange}>
        J'accepte de recevoir des infolettres occasionnels par courriel
      </Consent>
    </div>

    {error ? (
      <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
        {error}
      </div>
    ) : null}

    <button
      type="submit"
      disabled={!isComplete || isSubmitting}
      className="mt-5 w-full rounded-lg bg-yellow-600 py-3 text-base font-bold text-gray-900 transition hover:bg-yellow-500 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isSubmitting ? "Un instant..." : "M'abonner"}
    </button>
  </form>
);

export default SignupCard;
